import { useEffect, useState } from 'react'
import { Modal } from 'antd';
import PropTypes from "prop-types";
import axiosInstance from '../../../../variables/api/axiosInstance';
import SDIModal from "../../../../components/ui/SDIModal";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import AvenantAgencySelector from "./AvenantAgencySelector";
import { applyAvenantAgenceCommission } from "../../../../services/avenantCommissionService";
import useEnginReferentiels from "../../../../hooks/useEnginReferentiels";
import { MarqueSelect, TypeAutoComplete } from "../../../../components/engin/MarqueTypeFields";
import { coerceToCategorie } from "../../../../components/engin/categorieRules";

const AvenantDOrdreEngin = (props) => {
    const [loading, setLoading] = useState(false);
    const [contrat, setContrat] = useState(props.contrat);
    const [modalAvenantOrdre, setModalAvenantOrdre] = useState(props.open);
    const [engin, setEngin] = useState("");
    // Index de l'engin (occurrence) sélectionné dans la flotte — indispensable pour distinguer
    // un engin qui figure plusieurs fois (doublon issu d'une erreur de renouvellement).
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [errors, setErrors] = useState([{}]);
    // Agence ciblée + taux (siège) à appliquer au contrat résultant de l'avenant
    const [avCom, setAvCom] = useState({ agence: null, taux: null, intermediaire: null });
    const navigate = useNavigate();

    // Référentiels engin (source unique, partagée avec la création de contrat et les autres
    // avenants) : genre/carrosserie filtrés par la CATÉGORIE du contrat avec repli global +
    // dédup, et marques (/api/vehicule-marques) qui portent les MODÈLES du champ « Type ».
    const catCode = props?.contrat?.engins?.[0]?.categorie?.codeCategorie?.trim();
    const { genres, carosseries, marques } = useEnginReferentiels(catCode);

    // Genre/carrosserie hérités mais absents de la liste de la catégorie → élément par défaut.
    useEffect(() => {
        if (!genres.length && !carosseries.length) return;
        setEngin((prev) => {
            if (!prev) return prev;
            const genre = coerceToCategorie(prev.genre, genres);
            const carosserie = coerceToCategorie(prev.carosserie, carosseries);
            if (genre === prev.genre && carosserie === prev.carosserie) return prev;
            return { ...prev, genre, carosserie };
        });
    }, [genres, carosseries, selectedIndex]);

    useEffect(() => {
        const initialEngin = props?.contrat?.engins?.[0];
        setEngin(initialEngin ? { ...initialEngin } : initialEngin);
        setSelectedIndex(0);
        setModalAvenantOrdre(props.open);
        setContrat(props.contrat);
    }, [props.open]);

    // Changement d'engin dans le sélecteur : on charge l'occurrence ciblée (par index).
    const handleEnginSelect = (index) => {
        const target = props?.contrat?.engins?.[index];
        setSelectedIndex(index);
        setEngin(target ? { ...target } : target);
        setErrors([{}]);
    };

    const handleAssureChange = (e) => {
        const { name, value } = e.target;
        setEngin({ ...engin, [name]: value.toUpperCase() });
    };

    // Marque : on RÉINITIALISE le type — les modèles proposés dépendent de la marque.
    const handleMarqueChange = (value) => setEngin({ ...engin, marque: value, type: "" });
    const handleTypeChange = (value) => setEngin({ ...engin, type: value });

    const handleAvenantOrdre = () => {
        if (loading) {
            return;
        }
        if (!engin?.codeId) {
            toast("Aucun engin sélectionné pour l'avenant.");
            return;
        }

        // Modification TARIFANTE ? Le nombre de places et/ou la puissance impactent la prime : le back
        // génèrera un avenant OE re-tarifé (prime recalculée, ou AVOIR si baisse) au lieu d'un avenant
        // d'ordre administratif. On PRÉVIENT l'utilisateur avant d'envoyer.
        const originalEngin =
            props?.contrat?.engins?.[selectedIndex] ||
            props?.contrat?.engins?.find((item) => item?.codeId === engin?.codeId) || {};
        const numOrNull = (v) => { const n = Number.parseInt(v, 10); return Number.isNaN(n) ? null : n; };
        const nbPlaceChanged = engin?.nombrePlace !== undefined
            && numOrNull(engin.nombrePlace) !== null
            && numOrNull(engin.nombrePlace) !== numOrNull(originalEngin?.nombrePlace);
        // Valeur TARIFANTE effective = volume en priorité (le champ « Volume (ou Puissance) » édite
        // `volume`), sinon puissance — cohérent avec la RC côté back (pilotée par le volume).
        const tarifNew = numOrNull(engin?.volume ?? engin?.puissance);
        const tarifOld = numOrNull(originalEngin?.volume ?? originalEngin?.puissance);
        const puissanceVolumeChanged = tarifNew !== null && tarifNew !== tarifOld;

        if (nbPlaceChanged || puissanceVolumeChanged) {
            Modal.confirm({
                title: "Modification tarifante",
                width: 560,
                content: "Vous modifiez le nombre de places et/ou la puissance : ces caractéristiques "
                    + "impactent la tarification. Un avenant sera généré avec une prime RECALCULÉE "
                    + "(ou un AVOIR si la prime baisse), au lieu d'un simple avenant d'ordre sans prime. Continuer ?",
                okText: "Oui, re-tarifer",
                cancelText: "Annuler",
                onOk: () => doSubmitAvenantOrdre(),
            });
            return;
        }
        doSubmitAvenantOrdre();
    };

    const doSubmitAvenantOrdre = () => {
        setLoading(true);
        // On cible l'occurrence par INDEX (et non par codeId) pour gérer les doublons.
        const originalEngin =
            props?.contrat?.engins?.[selectedIndex] ||
            props?.contrat?.engins?.find((item) => item?.codeId === engin?.codeId) ||
            {};

        const parsedNombrePlace = Number.parseInt(engin?.nombrePlace, 10);
        const nombrePlace = Number.isNaN(parsedNombrePlace)
            ? originalEngin?.nombrePlace
            : parsedNombrePlace;

        const resolvedVolumeInput =
            engin?.volume ?? engin?.puissance ?? originalEngin?.volume ?? originalEngin?.puissance;
        const parsedVolume = Number.parseInt(resolvedVolumeInput, 10);
        const volume = Number.isNaN(parsedVolume)
            ? originalEngin?.volume
            : parsedVolume;

        // Si l'utilisateur a modifié le champ « Volume (ou Puissance) », la nouvelle valeur doit AUSSI
        // alimenter la puissance — sinon la CP d'un non-moto (qui affiche `puissance || volume`) continue
        // d'afficher l'ANCIENNE puissance alors que la tarification a pris le nouveau volume.
        const volumeEdited = engin?.volume !== undefined
            && String(engin?.volume ?? "") !== String(originalEngin?.volume ?? "");
        const puissance = (volumeEdited && volume !== undefined && volume !== null)
            ? String(volume)
            : (engin?.puissance ?? (volume !== undefined && volume !== null ? String(volume) : originalEngin?.puissance));

        const payload = {
            ...originalEngin,
            ...engin,
            nombrePlace,
            volume,
            puissance,
        };

        axiosInstance.put(`/api/contrat/ordre-engin?occurrenceIndex=${selectedIndex}`, payload).then(async (res) => {
            await applyAvenantAgenceCommission(res.data?.codeId, avCom.agence, avCom.taux, avCom.intermediaire);
            // Message selon le type d'avenant réellement généré côté back (OE re-tarifé vs AO administratif).
            // Un OE en PROPOSITION (statut EN_PROPOSITION) génère une prime → à valider puis régler en caisse.
            const estTarifant = res?.data?.avenant?.code === "OE";
            const estProposition = res?.data?.statutContrat === "EN_PROPOSITION";
            toast(estTarifant && estProposition
                ? "Avenant OE créé en PROPOSITION : à VALIDER puis RÉGLER en caisse avant impression."
                : estTarifant
                    ? "Modification tarifante appliquée : avenant OE re-tarifé généré."
                    : "Informations mises à jour. La prime du contrat est reconduite (avenant d'ordre, sans changement de prime).");
            props.update(false);
            const redirectCodeId = res?.data?.codeId || contrat?.codeId;
            if (redirectCodeId) {
                navigate(`/details-contrat/${redirectCodeId}`, { replace: true });
            }
        }).catch((err) => {
            console.log(err);
            toast("Erreur lors de l'avenant");
        }).finally(() => setLoading(false));
    }

    const handleCancel = () => {
        setModalAvenantOrdre(false);
        props.update(false);
    };

    return (
        <SDIModal
            title="AVENANT D'ORDRE - ENGIN"
            confirmLoading={loading}
            centered
            open={modalAvenantOrdre}
            onOk={() => handleAvenantOrdre()}
            onCancel={() => handleCancel()}
            width={900}
            zIndex={1500}>
            <AvenantAgencySelector
                organismeCode={props?.contrat?.organisme?.code}
                defaultAgence={props?.contrat?.courtier || props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                defaultIntermediaire={props?.contrat?.intermediaire}
                categorie={props?.contrat?.engins?.[0]?.categorie?.codeCategorie}
                onChange={(ag, t, inter) => setAvCom({ agence: ag, taux: t, intermediaire: inter })}
            />
            <form id="addNewAddressForm" className="row">
                <div className="col-12">
                    <div className="alert alert-info" role="alert">
                        Avenant d'ordre : on corrige les informations de l'engin (ou du souscripteur). La prime du contrat est <strong>reconduite à l'identique</strong> — pas de re-tarification.
                    </div>
                </div>
                <div className="col-12 mb-3">
                    <label className="form-label fw-bold" htmlFor="enginSelector">
                        Engin à modifier
                    </label>
                    <select
                        id="enginSelector"
                        className="form-control"
                        value={selectedIndex}
                        onChange={(e) => handleEnginSelect(Number(e.target.value))}
                    >
                        {props?.contrat?.engins?.map((eng, idx) => (
                            <option key={idx} value={idx}>
                                {`#${idx + 1} — ${eng?.immatriculation || "Sans immatriculation"} — ${eng?.marque || ""}${eng?.numeroChassis ? ` (Châssis: ${eng.numeroChassis})` : ""}`}
                            </option>
                        ))}
                    </select>
                    {props?.contrat?.engins?.length > 1 && (
                        <small className="text-muted d-block mt-1">
                            Flotte de {props.contrat.engins.length} engins. Sélectionnez celui à modifier.
                            Si un engin figure en double (erreur de renouvellement), modifier son
                            immatriculation ou son châssis créera un engin distinct.
                        </small>
                    )}
                </div>
                <div className="col-12 col-md-12">
                    <div className="row">
                        <div className="col-12 col-md-6">
                            <label className="form-label" htmlFor="marque">
                                Marque
                            </label>
                            <MarqueSelect
                                marques={marques}
                                value={engin?.marque}
                                onChange={handleMarqueChange}
                                style={{ width: "100%" }}
                                status={errors?.marque ? "error" : ""}
                            />
                            {errors?.marque && (
                                <div className="text-danger small mt-1">
                                    {errors?.marque}
                                </div>
                            )}
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label" htmlFor="nombrePlace">
                                Nombre de place
                            </label>
                            <input
                                type="text"
                                id="nombrePlace"
                                name="nombrePlace"
                                className={`form-control ${errors?.nombrePlace
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                placeholder="Entrez le nombre de place"
                                onChange={(e) => handleAssureChange(e)}
                                value={engin?.nombrePlace}
                            />
                            {errors?.nombrePlace && (
                                <div className="invalid-feedback">
                                    {errors?.nombrePlace}
                                </div>
                            )}
                            <small className="text-muted d-block mt-1">
                                Information corrigée sur l'engin ; la prime reste inchangée (reconduite).
                            </small>
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label" htmlFor="usage">
                                Usage
                            </label>
                            <select
                                id="usage"
                                name="usage"
                                value={engin?.usage || ""}
                                onChange={(e) => setEngin({ ...engin, usage: e.target.value })}
                                className={`form-control w-100 ${errors?.usage ? "is-invalid" : ""}`}
                                data-style="btn-default"
                            >
                                <option value="">Sélectionner…</option>
                                {(engin?.categorie?.nom || "").toUpperCase().startsWith("TRANSPORT") ? (
                                    <option value="TPV">TPV</option>
                                ) : (
                                    <>
                                        <option value="TAXI">Taxi</option>
                                        <option value="PRIVE">Privé</option>
                                    </>
                                )}
                            </select>
                            {errors?.usage && (
                                <div className="invalid-feedback">
                                    {errors?.usage}
                                </div>
                            )}
                            <small className="text-muted d-block mt-1">
                                Information corrigée sur l'engin ; la prime reste inchangée (reconduite).
                            </small>
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label" htmlFor="moteur">
                                Moteur
                            </label>
                            <input
                                type="text"
                                id="moteur"
                                name="moteur"
                                className={`form-control ${errors?.moteur
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                placeholder="Entrez le numéro du moteur"
                                onChange={(e) => handleAssureChange(e)}
                                value={engin?.moteur}
                            />
                            {errors?.moteur && (
                                <div className="invalid-feedback">
                                    {errors?.moteur}
                                </div>
                            )}
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label" htmlFor="type">
                                Type
                            </label>
                            <TypeAutoComplete
                                marques={marques}
                                marque={engin?.marque}
                                value={engin?.type}
                                onChange={handleTypeChange}
                                style={{ width: "100%" }}
                                status={errors?.type ? "error" : ""}
                            />
                            {errors?.type && (
                                <div className="text-danger small mt-1">
                                    {errors?.type}
                                </div>
                            )}
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label" htmlFor="immatriculation">
                                Immatriculation
                            </label>
                            <input
                                type="text"
                                id="immatriculation"
                                name="immatriculation"
                                className={`form-control ${errors?.immatriculation
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                placeholder="Entrez l'immatriculation de cet engin"
                                onChange={(e) => handleAssureChange(e)}
                                value={engin?.immatriculation}
                            />
                            {errors?.immatriculation && (
                                <div className="invalid-feedback">
                                    {errors?.immatriculation}
                                </div>
                            )}
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label" htmlFor="numeroChassis">
                                Numero de Châssis
                            </label>
                            <input
                                type="text"
                                id="numeroChassis"
                                name="numeroChassis"
                                className={`form-control ${errors?.numeroChassis
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                placeholder="Entrez le numero de chassis de cet engin"
                                onChange={(e) => handleAssureChange(e)}
                                value={engin?.numeroChassis}
                            />
                            {errors?.numeroChassis && (
                                <div className="invalid-feedback">
                                    {errors?.numeroChassis}
                                </div>
                            )}
                        </div>

                        <div className="col-12 col-md-6">
                            <label className="form-label" htmlFor="genre">
                                Genre du moteur
                            </label>
                            <select
                                id="genre"
                                name="genre"
                                value={engin?.genre || ""}
                                onChange={(e) =>
                                    setEngin({ ...engin, genre: e.target.value })
                                }
                                className={`form-control w-100 ${errors?.genre ? "is-invalid" : ""
                                    }`}
                                data-style="btn-default"
                            >
                                <option value="">Sélectionner…</option>
                                {genres?.map((g, i) => (
                                    <option key={i} value={g.libelleParametre}>
                                        {g.libelleParametre}
                                    </option>
                                ))}
                            </select>
                            {errors?.genre && (
                                <div className="invalid-feedback">
                                    {errors?.genre}
                                </div>
                            )}
                        </div>
                        {/* Carrosserie paramétrable PAR CATÉGORIE : affichée seulement si la catégorie
                            en propose (ex. catégories voiture ; absente pour les MOTO). */}
                        {carosseries?.length > 0 && (
                            <div className="col-12 col-md-6">
                                <label className="form-label" htmlFor="carosserie">
                                    Carrosserie
                                </label>
                                <select
                                    id="carosserie"
                                    name="carosserie"
                                    value={engin?.carosserie || ""}
                                    onChange={(e) => setEngin({ ...engin, carosserie: e.target.value })}
                                    className={`form-control w-100 ${errors?.carosserie ? "is-invalid" : ""
                                        }`}
                                    data-style="btn-default"
                                >
                                    <option value="">Sélectionner…</option>
                                    {carosseries?.map((c, i) => (
                                        <option key={i} value={c.libelleParametre}>
                                            {c.libelleParametre}
                                        </option>
                                    ))}
                                </select>
                                {errors?.carosserie && (
                                    <div className="invalid-feedback">
                                        {errors?.carosserie}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="col-12 col-md-6">
                            <label className="form-label" htmlFor="numeroChassis">
                                Volume (ou Puissance)
                            </label>
                            <input
                                type="number"
                                id="volume"
                                name="volume"
                                className={`form-control ${errors?.volume
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                placeholder="Entrez le volume (ou puissance) de cet engin"
                                onChange={(e) => handleAssureChange(e)}
                                value={engin?.volume || parseInt(engin?.puissance || 0) || 0}
                            />
                            {errors?.volume && (
                                <div className="invalid-feedback">
                                    {errors?.volume}
                                </div>
                            )}
                            <small className="text-muted d-block mt-1">
                                Valeur corrigée sur l'engin ; la prime reste inchangée (reconduite).
                            </small>
                        </div>
                    </div>
                </div>
            </form>
        </SDIModal>
    )
}

AvenantDOrdreEngin.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
}

export default AvenantDOrdreEngin
